import { ReactNode, useState, useId } from 'react';

export interface TabDef {
  key: string;
  label: ReactNode;
  badge?: ReactNode;
  content: ReactNode;
}

interface TabsProps {
  tabs: TabDef[];
  defaultKey?: string;
  className?: string;
}

export function Tabs({ tabs, defaultKey, className }: TabsProps) {
  const [active, setActive] = useState(defaultKey ?? tabs[0]?.key);
  const id = useId();

  return (
    <div className={`flex flex-col flex-1 min-h-0 ${className ?? ''}`}>
      <div role="tablist" className="flex border-b border-border bg-muted/30 px-2 shrink-0 overflow-x-auto">
        {tabs.map((tab) => {
          const isActive = active === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`${id}-panel-${tab.key}`}
              id={`${id}-tab-${tab.key}`}
              onClick={() => setActive(tab.key)}
              className={`relative px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                isActive
                  ? 'text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <span className="inline-flex items-center gap-1.5">
                {tab.label}
                {tab.badge}
              </span>
              {isActive && (
                <span className="absolute bottom-0 inset-x-0 h-0.5 bg-gold" />
              )}
            </button>
          );
        })}
      </div>

      {tabs.map((tab) => (
        <div
          key={tab.key}
          role="tabpanel"
          id={`${id}-panel-${tab.key}`}
          aria-labelledby={`${id}-tab-${tab.key}`}
          hidden={active !== tab.key}
          className={active === tab.key ? 'flex-1 min-h-0 flex flex-col' : ''}
        >
          {active === tab.key && tab.content}
        </div>
      ))}
    </div>
  );
}
